import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
} from 'reactstrap';
import api from '../../../services/api';
import ExportCSVModalView from './ExportCSVModal.view';

const GENERIC_EXPORT_ERROR = 'An error occurred while exporting the CSV file.';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const toIsoDate = (date) => date.toISOString().slice(0, 10);

const date30DaysBefore = (isoDate) => toIsoDate(new Date(Date.parse(isoDate) - 30 * DAY_IN_MS));

export const resolveDateRange = ({ from, to } = {}) => {
  const resolvedTo = to || toIsoDate(new Date());
  return {
    from: from || date30DaysBefore(resolvedTo),
    to: resolvedTo,
    isDefaultApplied: !from || !to,
  };
};

export default function (props) {
  const {
    sortBy,
    filters,
    search,
    isUat,
    products = [],
  } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportResult, setExportResult] = useState();
  // Deselections are tracked rather than selections so that every configured product starts checked
  // and a product list that changes underneath the dialog (an environment switch) needs no syncing.
  const [deselectedProducts, setDeselectedProducts] = useState([]);

  const selectedProducts = products.filter((code) => !deselectedProducts.includes(code));
  const dateRange = resolveDateRange(search);

  const handleModalClose = () => {
    // Closing is a no-op while an export is in flight. The request cannot be recalled, and clearing
    // `isExporting` here would re-enable the trigger and let a second export start alongside the
    // first — whose result would still arrive and still trigger its download. The body copy already
    // asks the user to keep the window open. Mirrors the guard in `resetState` in the V2 dialog.
    if (isExporting) return;

    setExportResult(null);
    setDeselectedProducts([]);
    setIsModalOpen(false);
  };

  // Any change to what would be exported invalidates the previous result, so the success copy goes
  // away and the export button becomes usable again.
  const handleProductToggle = (code) => {
    setExportResult(null);
    setDeselectedProducts((current) => (
      current.includes(code) ? current.filter((item) => item !== code) : [...current, code]
    ));
  };

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      const response = await api('RequestHistoryExport', {
        sortBy,
        filters,
        search: {
          ...search,
          from: dateRange.from,
        },
        isUat,
        products: selectedProducts,
      });
      const { url, error, errorMessage } = response;
      if (error || errorMessage) {
        const message = typeof error === 'string' ? error : errorMessage;
        setExportResult({
          error: typeof message === 'string' && message ? message : GENERIC_EXPORT_ERROR,
        });
        return;
      }

      if (!url) {
        setExportResult({ error: GENERIC_EXPORT_ERROR });
        return;
      }
      setExportResult({ url, success: true });
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', '');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setExportResult({ error: GENERIC_EXPORT_ERROR });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Button
        color="secondary"
        className="ml-2 d-flex"
        onClick={() => setIsModalOpen(true)}
        disabled={isExporting}
        outline
      >
        <span className="mr-2">Export CSV</span>
        <FontAwesomeIcon icon="download" />
      </Button>
      { isModalOpen && (
        <ExportCSVModalView
          dateRange={dateRange}
          products={products}
          selectedProducts={selectedProducts}
          isExporting={isExporting}
          exportResult={exportResult}
          onProductToggle={handleProductToggle}
          onClose={handleModalClose}
          onSubmit={handleExportCsv}
        />
      )}
    </>
  );
}
